// API helpers for Pharmacy Order Sets (Doctor Console → Order Management →
// Pharmacy tab's Order Set picker). Tenant/hospital scoping is resolved
// server-side from the ORG-CODE header (per backend team — see
// OrderManagementDrawer.js's PharmacyTab), so unlike some other IPD APIs we
// never send tenant_id / hospital_id from the client.

import axios from "axios";

const BASE =
  process.env.REACT_APP_BACKEND_ROUTE_URL ||
  "https://manage-dev.hlthtek.com/app/api/";

function authHeaders(extra = {}) {
  const token = sessionStorage.getItem("accessToken") || "";
  return {
    Authorization: `Bearer ${token}`,
    "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
    "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
    "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
    USERNAME: sessionStorage.getItem("userName") || "",
    ...extra,
  };
}

function currentUserId() {
  const v = sessionStorage.getItem("USER_ACCESS_KEY");
  return v && v !== "null" && v !== "undefined" ? v : null;
}

function errorMessage(err) {
  const data = err?.response?.data;
  if (typeof data?.detail === "string") return data.detail;
  if (typeof data?.detail?.message === "string") return data.detail.message;
  if (typeof data?.message === "string") return data.message;
  if (typeof data?.error === "string") return data.error;
  return err?.message || "Something went wrong. Please try again.";
}

// Catalogue stores generics flat ("Paracetamol 1000mg"); search_medicines/ and
// some item rows use brackets ("Paracetamol (1000mg)") or literal "+". Strip
// those so substitute lookup matches inventory — same idea as Care Plan's
// normalizeGenericTerm (keeps the strength, unlike prepareGenericSearchTerm).
function normalizeGenericTerm(raw) {
  return String(raw || "")
    .replace(/[()[\]{}]/g, " ")
    .replace(/\+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// encodeURIComponent uses %20 for spaces; axios' default URLSearchParams uses
// "+", which some backends treat as a literal plus in generic_name.
function encodePrescribeSearchParams(params = {}) {
  return Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== "")
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
    )
    .join("&");
}

// API: GET inventory/pharmacy/order-sets/
export async function listOrderSets() {
  try {
    const res = await axios.get(`${BASE}inventory/pharmacy/order-sets/`, {
      headers: authHeaders(),
    });
    return res?.data?.data ?? res?.data ?? [];
  } catch (err) {
    throw new Error(errorMessage(err));
  }
}

// API: GET inventory/pharmacy/order-sets/{orderSetId}/expand/
export async function expandOrderSet(orderSetId, { locationId } = {}) {
  try {
    const params = {};
    if (locationId) params.location_id = locationId;
    const res = await axios.get(
      `${BASE}inventory/pharmacy/order-sets/${orderSetId}/expand/`,
      { headers: authHeaders(), params },
    );
    return res?.data?.data ?? res?.data ?? [];
  } catch (err) {
    throw new Error(errorMessage(err));
  }
}

// API: GET inventory/pharmacy/prescribe/search/ — item-search shared by the
// Create Order Set form's Items section and the Pharmacy tab's medicine search
// (OrderManagementDrawer). Pass genericName instead of q to look up substitutes
// for an out-of-stock brand. Returns { items, hasMore } regardless of whether
// the backend paginates via a total/count or a bare array, so the caller never
// has to guess the response shape.
export async function searchPharmacyItems({
  q,
  genericName,
  dosageForm,
  availableOnly,
  locationId,
  limit,
  offset,
} = {}) {
  const effectiveLimit = limit ?? 50;
  const effectiveOffset = offset ?? 0;
  const params = { limit: effectiveLimit, offset: effectiveOffset };
  if (q) params.q = q;
  const cleanedGeneric = normalizeGenericTerm(genericName);
  if (cleanedGeneric) params.generic_name = cleanedGeneric;
  if (dosageForm) params.dosage_form = dosageForm;
  if (availableOnly) params.available_only = true;
  if (locationId) params.location_id = locationId;
  try {
    const res = await axios.get(`${BASE}inventory/pharmacy/prescribe/search/`, {
      headers: authHeaders(),
      params,
      paramsSerializer: encodePrescribeSearchParams,
    });
    const payload = res?.data?.data ?? res?.data ?? [];
    const items = Array.isArray(payload)
      ? payload
      : payload.results || payload.items || payload.data || [];
    const total = Array.isArray(payload)
      ? undefined
      : (payload.total ?? payload.count);
    const hasMore =
      typeof total === "number"
        ? effectiveOffset + items.length < total
        : items.length === effectiveLimit;
    return { items, hasMore };
  } catch (err) {
    throw new Error(errorMessage(err));
  }
}

// API: POST inventory/pharmacy/order-sets/
export async function createOrderSet({
  set_code,
  set_name,
  specialty,
  description,
  items,
}) {
  const body = {
    set_code,
    set_name,
    specialty: specialty || null,
    description: description || null,
    created_by: currentUserId(),
    items: (items || []).map((item, index) => ({
      item_id: item.item_id,
      default_frequency_code: item.default_frequency_code || null,
      default_quantity_per_dose: item.default_quantity_per_dose ?? null,
      default_days: item.default_days ?? null,
      uom_id: item.uom_id || null,
      sequence: item.sequence ?? index,
    })),
  };
  try {
    const res = await axios.post(
      `${BASE}inventory/pharmacy/order-sets/`,
      body,
      {
        headers: authHeaders({ "Content-Type": "application/json" }),
      },
    );
    return res?.data?.data ?? res?.data;
  } catch (err) {
    throw new Error(errorMessage(err));
  }
}

// API: GET inventory/uoms/ — master UOM list (id + name) for order-set item rows.
export async function listInventoryUoms() {
  try {
    const res = await axios.get(`${BASE}inventory/uoms/`, {
      headers: authHeaders(),
      params: { limit: 150 },
    });
    const payload = res?.data?.data ?? res?.data ?? [];
    const rows = Array.isArray(payload)
      ? payload
      : payload.results || payload.items || [];
    return rows
      .map((u) => ({
        uom_id: String(u.uom_id ?? u.id ?? ""),
        uom_name: u.uom_name ?? u.name ?? u.uom_code ?? "",
      }))
      .filter((u) => u.uom_id);
  } catch (err) {
    throw new Error(errorMessage(err));
  }
}
